'use client';

import { useState, useEffect } from "react";
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";

const STICKER_CONFIG = {
  "light-green": {
    src: "/img/sticker-green.svg",
    textClass: "text-light-green",
    textSticker: "text-dark-green",
    rotate: "-rotate-17",
    padding: "p-7 md:p-10",
  },
  "light-orange": {
    src: "/img/sticker-orange.svg",
    textClass: "text-light-orange",
    textSticker: "text-moyam-orange",
    rotate: "-rotate-16",
    padding: "p-6 md:p-12",
  },
  "dark-pink": {
    src: "/img/sticker-pink.svg",
    textClass: "text-dark-pink",
    textSticker: "text-light-pink",
    rotate: "rotate-23",
    padding: "p-4 md:p-10 mt-1 md:mt-1.5",
  },
  "default": {
    src: "/img/sticker-orange.svg",
    textClass: "text-light-orange",
    textSticker: "text-moyam-orange",
    rotate: "-rotate-16",
    padding: "p-12",
  }
};

const getStickerConfig = (stickerName) => {
  return STICKER_CONFIG[stickerName] || STICKER_CONFIG["default"];
};

const HighlightSpan = ({ children, isActive, textClass }) => {
  return (
    <span
      className={`font-bold transition-none ${isActive ? textClass : "text-beige"}`}
    >
      {children}
    </span>
  );
};

const StickerBadge = ({ text, isVisible, positionClass, stickerName }) => {
  if (!isVisible || !isFilled.richText(text)) return null;

  const config = getStickerConfig(stickerName);

  return (
    <div
      className={`
        absolute flex items-center justify-center text-center z-10 transition-opacity duration-300
        ${positionClass} ${config.textSticker}
        ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `}
    >
      <img
        src={config.src}
        alt=""
        className="absolute inset-0 w-full h-full object-contain -z-10 pointer-events-none"
      />

      <div className={`relative transform ${config.rotate} ${config.padding}`}>
        <PrismicRichText
          field={text}
          components={{
            paragraph: ({ children }) => (
              <p className={`text-sticker w-20 md:w-50`}>
                {children}
              </p>
            )
          }}
        />
      </div>
    </div>
  );
};

const TextWithSticker = ({ slice }) => {
  const line1 = slice.primary.line_1?.[0];
  const line2 = slice.primary.line_2?.[0];
  const line3 = slice.primary.line_3?.[0];

  const config1 = getStickerConfig(line1?.sticker);
  const config2 = getStickerConfig(line2?.sticker);
  const config3 = getStickerConfig(line3?.sticker);

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % 3);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const createComponents = (lineIndex, textClass) => ({
    heading1: ({ children }) => <h1 className="h1">{children}</h1>,
    heading2: ({ children }) => <h2 className="h1">{children}</h2>,
    heading3: ({ children }) => <h3 className="h1">{children}</h3>,
    label: ({ node, children }) => {
      if (node.data.label === "highlight") {
        return (
          <HighlightSpan
            isActive={activeIndex === lineIndex}
            textClass={textClass}
          >
            {children}
          </HighlightSpan>
        );
      }
      return <span className={node.data.label}>{children}</span>;
    },
  });

  return (
    <section
      className="bg-moyam-orange text-beige text-center py-28 md:py-50 xl:py-30 px-5 md:px-20 2xl:px-40 flex flex-col md:gap-10 overflow-hidden relative" // Ajout de relative ici au cas où
    >
      {/* Sticker 1 */}
      {isFilled.group(slice.primary.line_1) && line1 && (
        <StickerBadge
          text={line1.text}
          stickerName={line1.sticker}
          isVisible={activeIndex === 0}
          positionClass="top-4 left-4 md:top-7 xl:top-1/2 xl:-translate-y-1/2 xl:left-17 xl:mt-5"
        />
      )}

      {/* Sticker 2 */}
      {isFilled.group(slice.primary.line_2) && line2 && (
        <StickerBadge
          text={line2.text}
          stickerName={line2.sticker}
          isVisible={activeIndex === 1}
          positionClass="top-3 right-6  md:top-6 xl:top-1/2 xl:-translate-y-1/2 xl:right-24 xl:mt-4"
        />
      )}
      {/* Sticker 3 */}
      {isFilled.group(slice.primary.line_3) && line3 && (
        <StickerBadge
          text={line3.text}
          stickerName={line3.sticker}
          isVisible={activeIndex === 2}
          positionClass="bottom-2 md:bottom-7 left-10 xl:bottom-20 xl:left-30"
        />
      )}

      <div className="max-w-250 mx-auto relative w-full">
        <div className="flex flex-col z-20 relative">
          {/* LIGNE 1 */}
          {isFilled.group(slice.primary.line_1) && line1 && (
            <div className="flex justify-center items-center w-fit mx-auto">
              {isFilled.richText(line1.title) && (
                <PrismicRichText
                  field={line1.title}
                  components={createComponents(0, config1.textClass)}
                />
              )}
            </div>
          )}

          {/* LIGNE 2 */}
          {isFilled.group(slice.primary.line_2) && line2 && (
            <div className="flex justify-center items-center w-fit mx-auto">
              {isFilled.richText(line2.title) && (
                <PrismicRichText
                  field={line2.title}
                  components={createComponents(1, config2.textClass)}
                />
              )}
            </div>
          )}

          {/* LIGNE 3 */}
          {isFilled.group(slice.primary.line_3) && line3 && (
            <div className="flex justify-center items-center w-fit mx-auto ">
              {isFilled.richText(line3.title) && (
                <PrismicRichText
                  field={line3.title}
                  components={createComponents(2, config3.textClass)}
                />
              )}
            </div>
          )}
        </div>

      </div>
    </section>
  );
};

export default TextWithSticker;